import { CartModule } from './cart/cart.module';
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

//Material
import { MatBadgeModule } from '@angular/material/badge';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatSliderModule } from '@angular/material/slider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatOptionModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MomentDateModule } from '@angular/material-moment-adapter';
import { MatTreeModule } from '@angular/material/tree';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatPaginatorIntl } from '@angular/material/paginator';

import { LoginComponent } from './login/login.component';
import { StoreModule } from '@ngrx/store';
import { userReducer } from './redux/reducers/user.reducer';
import { NgxMaskModule, IConfig } from 'ngx-mask';

import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MainComponent } from './main/main.component';
import { CookieService } from 'ngx-cookie-service';
import { HeaderComponent } from './main/header/header.component';
import { ProductCodeSearchDialogComponent } from './main/header/product-code-search-dialog.component';
import { ReceivingQueueShortcutComponent } from './main/header/receiving-queue-shortcut.component';
import { ProcurementAttentionShortcutComponent } from './main/header/procurement-attention-shortcut.component';
import { DocumentQueueShortcutComponent } from './main/header/document-queue-shortcut.component';
import { ProductModule } from './product/product.module';
import { catalogueReducer } from './redux/reducers/catalogue.reducer';

import { cartReducer } from './redux/reducers/cart.reducer';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import { environment } from 'src/environments/environment';
import { BankModule } from './bank/bank.module';
import { SupportModule } from './support/support.module';
import { OrgModule } from './org/org.module';
import { WarehouseModule } from './warehouse/warehouse.module';
import { OrderStagingModule } from './order-staging/order-staging.module';
import { NewsModule } from './news/news.module';
import { GlobalErrorHandler } from './global-error-handler';
import { ServerErrorInterceptor } from './server-error-interceptor';
import { ComputerModule } from './computer/computer.module';
import { FiscalModule } from './fiscal/fiscal.module';
import { CityModule } from './city/city.module';
import { ClientModule } from './client/client.module';
import { LinkListComponent } from './main/link-list/link-list.component';
import { LinkListItemComponent } from './main/link-list-item/link-list-item.component';
import { MarkupTableComponent } from './markup-table/markup-table.component';
import { CreateComponent } from './markup-table/create/create.component';
import { MatSortModule } from '@angular/material/sort';
import { FolderModule } from './folder/folder.module';
import { BrandModule } from './brand/brand.module';
import { OrderModule } from './order/order.module';
import { RussianPaginatorIntl } from './shared/providers/russian-paginator.provider';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { LegacyCatalogRedirectComponent } from './legacy-catalog-redirect/legacy-catalog-redirect.component';
import { MarkupRulesTableModule } from './markup-table/rules-table/markup-rules-table.module';
import { CallJournalComponent } from './ringostat/call-journal/call-journal.component';
import { CallbackOutcomeDialogComponent } from './b2c-callback/callback-outcome-dialog.component';
import { B2cCallbackJournalComponent } from './b2c-callback/callback-journal.component';
import { B2cAnalyticsComponent } from './b2c-analytics/b2c-analytics.component';

const maskConfig: Partial<IConfig> = {
  validation: false
};

const config: SocketIoConfig = {
  url: environment.BACKEND,
  options: {
    transportOptions: {
      polling: {
        extraHeaders: {
          type: 'client'
        }
      }
    }
  }
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    MainComponent,
    HeaderComponent,
    ProductCodeSearchDialogComponent,
    ReceivingQueueShortcutComponent,
    ProcurementAttentionShortcutComponent,
    DocumentQueueShortcutComponent,
    LinkListComponent,
    LinkListItemComponent,
    MarkupTableComponent,
    CreateComponent,
    LegacyCatalogRedirectComponent,
    CallJournalComponent,
    B2cCallbackJournalComponent,
    CallbackOutcomeDialogComponent,
    B2cAnalyticsComponent
  ],
  imports: [
    ReactiveFormsModule,
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    FormsModule,
    MatBadgeModule,
    BrowserAnimationsModule,
    FolderModule,
    MatSliderModule,
    MatSortModule,
    BrandModule,
    MatGridListModule,
    MatToolbarModule,
    MatTooltipModule,
    MatIconModule,
    MatTreeModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatListModule,
    MatCardModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatTableModule,
    MomentDateModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    MatButtonToggleModule,
    MatDialogModule,
    MarkupRulesTableModule,
    ProductModule,
    MatChipsModule,
    MatSelectModule,
    MatOptionModule,
    OrgModule,
    SupportModule,
    ComputerModule,
    CartModule,
    BankModule,
    FiscalModule,
    ClientModule,
    WarehouseModule,
    OrderStagingModule,
    CityModule,
    NewsModule,
    OrderModule,
    NgxMaskModule.forRoot(maskConfig),
    SocketIoModule.forRoot(config),
    StoreModule.forRoot({
      user: userReducer,
      catalogue: catalogueReducer,
      cart: cartReducer
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      autoPause: true // Pauses recording actions and state changes when the extension window is not open
    })
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' }
    },
    { provide: MAT_DATE_LOCALE, useValue: 'ru-RU' },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD.MM.YYYY'
        },
        display: {
          dateInput: 'DD.MM.YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM-YYYY'
        }
      }
    },
    CookieService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
    { provide: MatPaginatorIntl, useClass: RussianPaginatorIntl },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
